import styles from './segments-page.module.scss';
import React, { useEffect, useState } from 'react';
import ActionButton from '../../atoms/Base/ActionButton/action-button';
import { useNavigate } from 'react-router-dom';
import { PrimaryRoutes } from '../../../common/enums/internal-route-enums';
import { SegmentView } from '../../../models/segments/segment-view';
import {
	useReferenceValuesApiService,
	useSegmentsApiService,
} from '../../../api/providers/AppServiceProvider';
import { ArrayHelper } from '../../../common/helpers/array-helper';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setCachedSegmentViewContactCount, setCachedSegmentViews } from '../../../features/segments-slice';
import { StringHelper } from '../../../common/helpers/string-helper';
import { Container } from '../../atoms/Base/Container/Container';
import Label from '../../atoms/Base/Label/Label';
import CXDataLoader from '../../atoms/Custom/CXDataLoader/CXDataLoader';
import SegmentsTableTemplate from '../../templates/SegmentsTableTemplate/SegmentsTableTemplate';
import TextBox from '../../atoms/Base/TextBox/TextBox';
import { StringValueCallback } from '../../../types/Callbacks';
import { setCachedContactColumnNames } from '../../../features/contacts-slice';
import { ReferenceValuesHelper } from '../../../common/helpers/reference-values-helper';
import { setCachedSelectedMedianColumns } from '../../../features/median-values-slice';
import { FieldType } from '../../../common/enums/field-type-enums';

export default function SegmentsPage() {
	const dispatch = useAppDispatch();
	const cachedContactColumnNames = useAppSelector((state) => state.contacts.columnNames);
	const cachedRefreshState = useAppSelector((state) => state.segments.refresh);
	const referenceValuesApiService = useReferenceValuesApiService();
	const segmentsApiService = useSegmentsApiService();

	const navigate = useNavigate();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [segmentViews, setSegmentViews] = useState<SegmentView[]>([]);
	const [searchTerm, setSearchTerm] = useState<string>(StringHelper.Empty);
	const hasLoadedRef = React.useRef(false);
	const previousRefreshState = React.useRef(cachedRefreshState);

	useEffect(() => {
		// Only load if:
		// 1. First time loading (!hasLoadedRef.current)
		// 2. Refresh state changed (cachedRefreshState !== previousRefreshState.current)
		const shouldLoad = !hasLoadedRef.current || cachedRefreshState !== previousRefreshState.current;
		
		if (!shouldLoad) {
			return;
		}

		const loadSegments = async () => {
			try {
				// Set loading to true at the start
				setIsLoading(true);
				
				// Load column names if not cached
				if (!ArrayHelper.HasElements(cachedContactColumnNames)) {
					const columnNames = await referenceValuesApiService.GetContactColumnNames();
					dispatch(setCachedContactColumnNames(columnNames));
					dispatch(
						setCachedSelectedMedianColumns(
							ReferenceValuesHelper.GetColumnsByType(columnNames, FieldType.Number, 3)
						)
					);
				} else {
					dispatch(
						setCachedSelectedMedianColumns(
							ReferenceValuesHelper.GetColumnsByType(cachedContactColumnNames, FieldType.Number, 3)
						)
					);
				}

				// Reset segment view contact count
				dispatch(setCachedSegmentViewContactCount(0));
				
				// Load segments
				const response = await segmentsApiService.GetSegmentViews();
				const segmentViews = response.map((segmentView: SegmentView) => {
					const segmentNameParts = segmentView.segmentName.split('|');
					const testVariantPercentages = segmentNameParts.slice(1, 5);
					return {
						...segmentView,
						contactCount: segmentNameParts.length === 7 ? Number(segmentNameParts[6]) : 0,
						segmentName: segmentNameParts[0],
						segmentType:
							segmentNameParts.length === 7
								? segmentNameParts[5]
								: segmentNameParts.length === 2
								? segmentNameParts[1]
								: StringHelper.Empty,
						testVariantPercentages:
							ArrayHelper.HasElements(testVariantPercentages) && testVariantPercentages.length === 4
								? testVariantPercentages
								: undefined,
					};
				});
				
				dispatch(setCachedSegmentViews(segmentViews));
				setSegmentViews(segmentViews);
				
				// Mark as loaded and update previous refresh state
				hasLoadedRef.current = true;
				previousRefreshState.current = cachedRefreshState;
			} catch (error) {
				console.error('Error loading segments:', error);
			} finally {
				// Always set loading to false when done
				setIsLoading(false);
			}
		};

		loadSegments();
	}, [cachedRefreshState, dispatch, segmentsApiService, referenceValuesApiService, cachedContactColumnNames]);

	const onChangeHandler: StringValueCallback = (newValue: string) => {
		setSearchTerm(newValue.toLowerCase());
	};

	return (
		<Container className='p-4'>
			<Container horizontal left childSpaceLarge>
				<ActionButton tall onClick={() => navigate(PrimaryRoutes.SegmentBuilder)}>
					Create a New Segment
				</ActionButton>
				<Container left>
					<TextBox showSearchIcon placeHolder={'Search'} onChange={onChangeHandler} />
				</Container>
			</Container>
			<Container leftSpace rightSpace topSpace bottomSpace>
				<Label className='m-4'>Segment Details</Label>
			</Container>
			<Container>
				{isLoading ? (
					<div className="flex items-center justify-center min-h-[400px] w-full">
						<CXDataLoader />
					</div>
				) : (
					segmentViews && segmentViews.length > 0 ? (
						<Container className={styles.segments_container} childSpace>
							<Container>
								<SegmentsTableTemplate segmentViews={segmentViews} searchTerm={searchTerm} />
							</Container>
						</Container>
					) : (
						<div className="flex items-center justify-center min-h-[400px] w-full">
							<div className="text-center text-gray-500">
								<p>No segments found.</p>
								<p className="text-sm mt-2">Create your first segment to get started.</p>
							</div>
						</div>
					)
				)}
			</Container>
		</Container>
	);
}
